import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Settings, Plus, Trash2, Loader2, RotateCcw, GripVertical } from "lucide-react";

/**
 * Reusable admin-only taxonomy manager (Phase 57).
 *
 * Renders a small gear-icon button visible ONLY to founder/admin. Clicking
 * opens a Dialog where they can add new items, edit labels inline, soft-
 * delete (deactivate) existing items, or restore previously-soft-deleted ones.
 *
 * Props:
 *   kind             — namespace, must be a key in backend ALLOWED_KINDS
 *                      (e.g. "expense_categories", "partner_specialties").
 *   title            — heading shown in the modal.
 *   description      — short sub-heading shown in the modal.
 *   triggerLabel     — tooltip on the gear button (defaults to title).
 *   compact          — when true, renders only the gear icon (no label).
 *   onChange         — optional callback fired after any add/edit/delete so
 *                      the parent page can refresh its dropdown.
 */
const TaxonomyManager = ({
  kind,
  title,
  description,
  triggerLabel,
  compact = true,
  onChange,
}) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  // Founder-only — per Phase B.2 spec, even master admins don't see the inline
  // gear icon. They can manage taxonomies from /admin?tab=lists.
  const isFounder = !!user && (
    (user.email || "").toLowerCase() === "ramneetrdh@gmail.com" ||
    user.admin_role === "founder" ||
    user.role === "founder"
  );

  const fetchItems = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/taxonomy/${kind}`);
      setItems(res.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load items");
    } finally {
      setLoading(false);
    }
  }, [open, kind]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  if (!isFounder) return null;

  const handleAdd = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setAdding(true);
    try {
      await axios.post(`${API}/admin/taxonomy/${kind}`, { label });
      setNewLabel("");
      await fetchItems();
      if (onChange) onChange();
      toast.success(`Added "${label}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  const handleSaveEdit = async (item) => {
    if (!editLabel.trim() || editLabel === item.label) {
      setEditingId(null);
      return;
    }
    setBusyId(item.taxonomy_id);
    try {
      await axios.put(`${API}/admin/taxonomy/${kind}/${item.taxonomy_id}`, { label: editLabel.trim() });
      setEditingId(null);
      await fetchItems();
      if (onChange) onChange();
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.label}" from this list? Existing content tagged with this will still show, but members won't be able to pick it for new content.`)) return;
    setBusyId(item.taxonomy_id);
    try {
      await axios.delete(`${API}/admin/taxonomy/${kind}/${item.taxonomy_id}`);
      await fetchItems();
      if (onChange) onChange();
      toast.success("Removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    } finally {
      setBusyId(null);
    }
  };

  const handleRestore = async (item) => {
    setBusyId(item.taxonomy_id);
    try {
      await axios.put(`${API}/admin/taxonomy/${kind}/${item.taxonomy_id}`, { is_active: true });
      await fetchItems();
      if (onChange) onChange();
      toast.success("Restored");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to restore");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center gap-1.5 ${compact ? "px-2 py-1.5 text-xs" : "px-3 py-1.5 text-sm"} rounded-full text-[#6B4E8A] hover:bg-[#F0ECF7] transition-colors`}
        title={triggerLabel || title || "Manage list"}
        data-testid={`taxonomy-manage-${kind}`}
      >
        <Settings className="w-3.5 h-3.5" />
        {!compact && <span>Manage</span>}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid={`taxonomy-modal-${kind}`}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>

          {/* Add new */}
          <div className="flex items-center gap-2 mt-2">
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              placeholder="New item label…"
              className="flex-1 bg-white border-[#E5DFEC]"
              data-testid={`taxonomy-new-input-${kind}`}
            />
            <Button
              onClick={handleAdd}
              disabled={adding || !newLabel.trim()}
              className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full px-4"
              data-testid={`taxonomy-add-btn-${kind}`}
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Add</>}
            </Button>
          </div>

          {/* List */}
          {loading ? (
            <div className="flex items-center justify-center py-8 text-[#94A3B8] text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <ul className="space-y-1.5 mt-4 pr-1" data-testid={`taxonomy-list-${kind}`}>
              {items.length === 0 && (
                <li className="text-sm text-[#94A3B8] py-4 text-center">No items yet — add one above.</li>
              )}
              {items.map((item) => (
                <li
                  key={item.taxonomy_id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${
                    item.is_active ? "border-[#E5DFEC] bg-white" : "border-[#FECACA] bg-[#FEF2F2]"
                  }`}
                >
                  <GripVertical className="w-3.5 h-3.5 text-[#CBD5E1] shrink-0" />
                  {editingId === item.taxonomy_id ? (
                    <Input
                      autoFocus
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onBlur={() => handleSaveEdit(item)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveEdit(item);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      className="flex-1 h-8 text-sm"
                      data-testid={`taxonomy-edit-input-${item.taxonomy_id}`}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setEditingId(item.taxonomy_id); setEditLabel(item.label); }}
                      className={`flex-1 text-left text-sm ${item.is_active ? "text-[#475569]" : "text-[#94A3B8] line-through"}`}
                      data-testid={`taxonomy-item-${item.taxonomy_id}`}
                    >
                      {item.label}
                      <span className="ml-2 text-[10px] text-[#94A3B8] uppercase tracking-wider">{item.key}</span>
                    </button>
                  )}

                  {item.is_active ? (
                    <button
                      type="button"
                      onClick={() => handleDelete(item)}
                      disabled={busyId === item.taxonomy_id}
                      className="p-1.5 rounded-lg text-[#EF4444] hover:bg-[#FEE2E2] transition-colors disabled:opacity-50"
                      title="Remove"
                      data-testid={`taxonomy-delete-${item.taxonomy_id}`}
                    >
                      {busyId === item.taxonomy_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleRestore(item)}
                      disabled={busyId === item.taxonomy_id}
                      className="p-1.5 rounded-lg text-[#6B4E8A] hover:bg-[#F0ECF7] transition-colors disabled:opacity-50"
                      title="Restore"
                      data-testid={`taxonomy-restore-${item.taxonomy_id}`}
                    >
                      {busyId === item.taxonomy_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <p className="text-[11px] text-[#94A3B8] mt-3">
            Deletions are soft — existing content tagged with these keys keeps rendering. Click ↺ to restore a removed item.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TaxonomyManager;
